import { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { logout } from '../redux/Userslice';

export const useAutoLogout = (themeOutlet, idleTimeout = 2 * 60 * 1000) => { // default 2 minutes for testing
  const dispatch = useDispatch();
  const lastTokenRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const warningShownRef = useRef(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;

    let userData;
    try {
      userData = JSON.parse(storedUser);
    } catch {
      dispatch(logout());
      return;
    }

    const token = userData?.token;
    if (!token) return;

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch {
      dispatch(logout());
      return;
    }

    const expiryTime = decoded.exp * 1000;
    lastTokenRef.current = token;

    const logoutNow = () => {
      Swal.fire({
        text: 'You were inactive for too long. Logging out.',
        showConfirmButton: false,
        timer: 2500,
        customClass: {
          popup: `${themeOutlet} custom-swal-popup`
        }
      }).then(() => dispatch(logout()));
    };

    const showWarningModal = () => {
      warningShownRef.current = true;

      Swal.fire({
        title: 'Session Timeout Warning',
        html: 'You will be logged out in <b><span id="countdown">60</span></b> seconds due to inactivity.',
        showCancelButton: true,
        confirmButtonText: 'Stay Logged In',
        cancelButtonText: 'Logout Now',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: `${themeOutlet} custom-swal-popup`
        },
        didOpen: () => {
          let countdownEl = Swal.getHtmlContainer().querySelector('#countdown');
          let timeLeft = 60;
          Swal._timerInterval = setInterval(() => {
            timeLeft -= 1;
            if (countdownEl) countdownEl.textContent = timeLeft;
            if (timeLeft <= 0) {
              clearInterval(Swal._timerInterval);
              Swal.close();
              logoutNow();
            }
          }, 1000);
        },
        willClose: () => {
          clearInterval(Swal._timerInterval);
        }
      }).then((result) => {
        if (result.isConfirmed) {
          resetIdleTimer(); // User stayed
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          logoutNow(); // User chose to logout
        }
      });
    };

    const resetIdleTimer = () => {
      clearTimeout(activityTimeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
      warningShownRef.current = false;

      warningTimeoutRef.current = setTimeout(() => {
        showWarningModal();
      }, idleTimeout - 60 * 1000); // show warning at 1 min before logout

      activityTimeoutRef.current = setTimeout(() => {
        if (!warningShownRef.current) {
          logoutNow();
        }
      }, idleTimeout);
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer(); // initial

    const intervalId = setInterval(() => {
      const latest = localStorage.getItem('user');
      const latestToken = latest ? JSON.parse(latest)?.token : null;

      if (latestToken !== lastTokenRef.current) {
        window.location.reload(); // token changed in another tab
        return;
      }

      if (Date.now() > expiryTime) {
        console.log("checking",Date.now() > expiryTime);
        
        Swal.fire({
          text: 'Your session has expired! Please login again.',
          showConfirmButton: false,
          timer: 2500,
          customClass: {
            popup: `${themeOutlet} custom-swal-popup`
          }
        }).then(() => dispatch(logout()));
        clearInterval(intervalId);
      }
    }, 5000);

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
      clearTimeout(activityTimeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
      clearInterval(intervalId);
    };
  }, [dispatch, themeOutlet, idleTimeout]);
};
